import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import EditBusiness from "./forms/EditBusiness";
import UploadLogo from "./forms/UploadLogo";
import UploadBanner from "./forms/UploadBanner";
import { getAgencyAddress } from "./agencyAddress";
import { useBusiness, usePermaLink, updatePermaLink } from "@/_actions/businessActions";
import { useProfile } from "@/_actions/userActions";
import Notification from "@/components/overlays/Notification";
import { usePlan } from "@/_actions/authActions";
import { InformationCircleIcon, LinkIcon } from "@heroicons/react/outline";
import { buildPortalUrl } from "@/utils/buildPortalUrl";
import { handleApiError } from "@/utils/errorHandler";
import { Button } from "@/components/ui/button";
import EditIcon from "./img/edit.svg";
import {
  cardClassName,
  cardTitleClassName,
  errorClassName,
  fieldClassName,
  fieldGridClassName,
  labelClassName,
  outlineButtonClassName,
  primaryButtonClassName,
} from "./styles";

// Same rule the backend applies to the agency page: its URL and banner need owner/admin and cyt_landing.
function canManageAgencyPage(role, plan) {
  return ["admin", "owner"].includes(role) && Boolean(plan?.plan_addons?.cyt_landing);
}

export default function BusinessProfile() {
  const { data: profile, mutate: mutateProfile } = useProfile();
  const { data = {}, mutate: mutateBusiness } = useBusiness(profile?.client);
  const { data: planData } = usePlan();
  const [edit, toggleEdit] = useState(false);
  const [showNotification, toggleNotification] = useState(false);
  const [textNotification, setTextNotification] = useState("");
  const [showBanner, setShowBanner] = useState(false);
  useEffect(() => {
    if (data?.business_name == "" || profile?.logo_client == "") {
      setShowBanner(true);
      toggleEdit(true);
    } else {
      setShowBanner(false);
    }
  }, [profile, data]);

  const role = profile?.role;

  const handleToggleNotification = (text) => {
    setTextNotification(text);
    toggleNotification(true);
    setTimeout(() => {
      toggleNotification(false);
    }, 5000);
  };

  const heroImage = data?.hero_image;
  const banner = (
    <UploadBanner
      // Keys keep each instance when the banner moves above/below the logo.
      key="banner"
      clientId={profile?.client}
      heroImage={heroImage}
      canEdit={canManageAgencyPage(role, planData?.plan)}
      onUploaded={(url) => {
        mutateBusiness((current) => ({ ...current, hero_image: url }));
        handleToggleNotification("Banner actualizado con éxito");
      }}
      // A failed or unconfirmed upload may still have been saved: reload the real state.
      onUploadFailed={() => mutateBusiness()}
      onRemoved={() => {
        mutateBusiness((current) => ({ ...current, hero_image: null }));
        handleToggleNotification("Banner quitado correctamente");
      }}
      onRemoveFailed={() => mutateBusiness()}
    />
  );
  const logo = (
    <UploadLogo
      key="logo"
      logoClient={profile?.logo_client}
      callBack={() => {
        mutateProfile();
        mutateBusiness();
        handleToggleNotification("Cambios realizados con éxito");
      }}
      clientId={profile?.client}
    />
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-9">
        <h2 className={cardTitleClassName}>Datos de la inmobiliaria</h2>
        {!edit && ["admin", "owner"].includes(role) ? (
          <Button
            onClick={() => toggleEdit(true)}
            type="button"
            variant="outline"
            aria-label="Editar datos de la inmobiliaria"
            data-splitbee-event="account-edit-contact"
            className={outlineButtonClassName}
          >
            <img src={EditIcon} alt="" width="24" height="24" />
            Editar
          </Button>
        ) : null}
      </div>
      {showBanner && (
        <div className="flex w-full rounded-lg border border-[#3655E0] bg-[#E7EBFA] p-2 font-bold text-[#0C2078]">
          <InformationCircleIcon className="mr-1 w-5" />
          Completa los datos👇 para publicar propiedades y comienza a recibir clientes potenciales
          👥 🎯
        </div>
      )}
      {/* Figma: the banner goes above the logo once it exists, below it while empty. */}
      {heroImage ? (
        <>
          {banner}
          {logo}
        </>
      ) : (
        <>
          {logo}
          {banner}
        </>
      )}
      {!edit ? (
        <BusinessDetails data={data} />
      ) : (
        <EditBusiness
          data={data}
          callBack={() => {
            toggleEdit(false);
            mutateBusiness();
            handleToggleNotification("Cambios realizados con éxito");
          }}
          firstLogin={showBanner}
          cancel={() => toggleEdit(false)}
        />
      )}
      <Notification open={showNotification} setOpen={toggleNotification} title={textNotification} />
    </div>
  );
}

function BusinessDetails({ data }) {
  const address = getAgencyAddress(data?.address);
  const fields = [
    { label: "Nombre", value: data?.business_name, className: "md:col-span-2 font-bold" },
    { label: "Email", value: data?.email_contact },
    { label: "Teléfono", value: data?.whatsapp },
    { label: "Calle", value: address?.street },
    { label: "Colonia", value: address?.neighborhood?.name },
    { label: "Número exterior", value: address?.num_exterior },
    { label: "Número interior", value: address?.num_interior },
    { label: "Estado", value: address?.state?.name },
    { label: "Municipio", value: address?.municipality?.name },
  ];

  return (
    <dl className={fieldGridClassName}>
      {fields.map(({ label, value, className = "" }) => (
        <div key={label} className={className}>
          <dt className={`${labelClassName} font-normal`}>{label}</dt>
          <dd className={`${fieldClassName} break-words`}>{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function BusinessPageUrl() {
  const { data: profile } = useProfile();
  const { data: planData } = usePlan();
  const { plan } = planData || {};
  const [showNotification, toggleNotification] = useState(false);

  if (!canManageAgencyPage(profile?.role, plan)) {
    return null;
  }

  const handleSaved = () => {
    toggleNotification(true);
    setTimeout(() => {
      toggleNotification(false);
    }, 5000);
  };

  return (
    <div className={cardClassName}>
      <PermaLinkForm callBack={handleSaved} />
      <Notification
        open={showNotification}
        setOpen={toggleNotification}
        title="Url actualizada con éxito"
      />
    </div>
  );
}

function PermaLinkForm({ callBack }) {
  const { data, isLoading, error, mutate } = usePermaLink();
  const [editor, setEditor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const saveInFlight = useRef(false);
  const savedPermalink = typeof data?.permalink === "string" ? data.permalink : "";
  const isEditing = editor !== null;
  const hasChanges = isEditing && editor.value !== editor.initialValue;
  const canSave = hasChanges && Boolean(editor.value.trim()) && !isSaving;
  const savedUrl = savedPermalink.trim()
    ? buildPortalUrl(`/inmobiliaria/${encodeURIComponent(savedPermalink)}`)
    : "";
  const urlPrefix = buildPortalUrl("/inmobiliaria/");
  const canVisit = Boolean(savedUrl) && !hasChanges && !isSaving;

  const startEditing = () => {
    setEditor({ value: savedPermalink, initialValue: savedPermalink });
    setSaveError("");
  };

  const cancelEditing = () => {
    setEditor(null);
    setSaveError("");
  };

  const copyPublishedUrl = async () => {
    if (!canVisit) return;

    try {
      await navigator.clipboard.writeText(savedUrl);
      toast.success("URL copiada al portapapeles");
    } catch {
      toast.error("No pudimos copiar la URL. Puedes seleccionarla y copiarla manualmente.");
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canSave || saveInFlight.current) return;

    saveInFlight.current = true;
    setIsSaving(true);
    setSaveError("");
    try {
      const confirmed = await updatePermaLink({ permalink: editor.value });
      if (typeof confirmed?.permalink !== "string" || !confirmed.permalink.trim()) {
        setSaveError("No se pudo confirmar la URL guardada. Intenta de nuevo.");
        return;
      }
      await mutate(confirmed, false);
      setEditor(null);
      callBack();
    } catch (err) {
      const fieldError = err.response?.data?.permalink;
      setSaveError(
        Array.isArray(fieldError)
          ? fieldError.join(" ")
          : typeof fieldError === "string"
            ? fieldError
            : handleApiError(err).userMessage,
      );
    } finally {
      saveInFlight.current = false;
      setIsSaving(false);
    }
  };

  const failedToLoad = Boolean(error && !data);
  const displayPrefix = urlPrefix.replace(/^https?:\/\//, "");
  const describedBy = [
    "business-permalink-hint",
    "business-permalink-help",
    saveError && "business-permalink-error",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section aria-labelledby="business-page-title" className="space-y-4">
      <div className="flex items-start gap-9">
        <h2 id="business-page-title" className={cardTitleClassName}>
          URL inmobiliaria
        </h2>
        {!isLoading && !failedToLoad && !isEditing ? (
          <Button
            type="button"
            variant="outline"
            aria-label="Editar URL"
            onClick={startEditing}
            className={outlineButtonClassName}
          >
            <img src={EditIcon} alt="" width="24" height="24" />
            Editar
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <p role="status" className="text-sm text-[#7b7b7b]">
          Cargando URL de la inmobiliaria…
        </p>
      ) : failedToLoad ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p role="alert" className="text-sm text-red-700">
            No pudimos cargar la URL de tu inmobiliaria. Intenta de nuevo.
          </p>
          <Button
            type="button"
            variant="outline"
            className={`${outlineButtonClassName} mt-2`}
            onClick={() => mutate().catch(() => {})}
          >
            Reintentar
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} aria-busy={isSaving}>
          <p id="business-permalink-hint" className={labelClassName}>
            Crea una página personalizada para tus anuncios ejemplo{" "}
            <span className="text-black">mi-pagina</span>
          </p>
          <div className="flex min-w-0 flex-col sm:flex-row">
            <span className="break-all rounded-t-lg border border-[#a9b1bc] bg-[#ecf1f1] p-2 text-base text-[#747070] sm:shrink-0 sm:rounded-l-lg sm:rounded-tr-none">
              {displayPrefix}
            </span>
            {isEditing ? (
              <input
                type="text"
                name="permalink"
                id="business-permalink"
                value={editor.value}
                className={`${permalinkValueClassName} focus:border-[#4f85f0] focus:outline-none focus:ring-1 focus:ring-[#4f85f0] disabled:bg-gray-50 disabled:text-gray-500`}
                disabled={isSaving}
                required
                aria-labelledby="business-page-title"
                aria-invalid={Boolean(saveError)}
                aria-describedby={describedBy}
                onChange={(event) => {
                  setEditor({ ...editor, value: event.target.value });
                  setSaveError("");
                }}
              />
            ) : (
              <span className={`${permalinkValueClassName} break-all`}>
                {savedUrl ? (
                  savedUrl.slice(urlPrefix.length)
                ) : (
                  <span className="text-[#7b7b7b]">Sin URL configurada</span>
                )}
              </span>
            )}
          </div>
          {isEditing ? (
            <p id="business-permalink-help" className="mt-1 text-sm text-[#7b7b7b]">
              {isSaving
                ? "Guardando tu nueva URL…"
                : hasChanges
                  ? "Guarda los cambios para visitar la nueva URL."
                  : "Edita el nombre al final de la dirección para personalizar tu URL."}
            </p>
          ) : null}
          {saveError && (
            <p id="business-permalink-error" role="alert" className={errorClassName}>
              {saveError}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {isEditing ? (
              <>
                <Button type="submit" disabled={!canSave} className={primaryButtonClassName}>
                  {isSaving ? "Guardando…" : "Guardar"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving}
                  onClick={cancelEditing}
                  className={outlineButtonClassName}
                >
                  Cancelar
                </Button>
              </>
            ) : null}
            {canVisit ? (
              <a className={visitLinkClassName} rel="noreferrer" target="_blank" href={savedUrl}>
                Ir a la página de la inmobiliaria
              </a>
            ) : (
              <button type="button" className={visitLinkClassName} disabled>
                Ir a la página de la inmobiliaria
              </button>
            )}
            <Button
              type="button"
              variant="outline"
              aria-label="Copiar URL de la inmobiliaria"
              title="Copiar URL de la inmobiliaria"
              onClick={copyPublishedUrl}
              disabled={!canVisit}
              className={outlineButtonClassName}
            >
              <LinkIcon aria-hidden="true" />
              Copiar URL
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

const permalinkValueClassName =
  "block min-h-10 w-full min-w-0 rounded-b-lg border border-t-0 border-[#a9b1bc] bg-white p-2 text-base text-black sm:flex-1 sm:rounded-r-lg sm:rounded-bl-none sm:border-t sm:border-l-0";

const visitLinkClassName =
  "inline-flex min-h-10 items-center rounded-lg border border-[#4f85f0] bg-[#e3ecfd] p-2 text-base text-black transition-colors hover:bg-[#d3e1fc] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f85f0] disabled:cursor-not-allowed disabled:opacity-50";
